import { useEffect, useState } from 'react';
import PlayerControls from '../PlayerControls';
import { formatDuration } from '../../utils/chapterPlayback';

function AudioPlayerOverlay({
  audioRef,
  chapterOrder,
  title,
  summary,
  sourceUrl = '',
  audioUrl,
  autoPlay,
  isMarkedAsPlayed,
  isOwner = false,
  onShare = null,
  onClose,
  onEnded,
  onPlay,
  onPause,
  onMarkAsPlayed,
  onMarkAsUnplayed,
  onPrev = null,
  onNext = null,
  hasPrev = false,
  hasNext = false,
}) {
  // Mirrors of the <audio> element's state, kept in sync by listening to it —
  // the native controls are replaced by PlayerControls plus a seek slider.
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return undefined;
    const sync = () => {
      setIsPlaying(!audio.paused && !audio.ended);
      setCurrentTime(audio.currentTime || 0);
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    sync();
    const events = ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata', 'durationchange'];
    events.forEach((e) => audio.addEventListener(e, sync));
    return () => events.forEach((e) => audio.removeEventListener(e, sync));
  }, [audioRef, audioUrl]);

  if (!audioUrl) return null;

  const play = () => {
    const p = audioRef?.current?.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  const pause = () => audioRef?.current?.pause();

  const seekTo = (seconds) => {
    const audio = audioRef?.current;
    if (!audio) return;
    try {
      audio.currentTime = seconds;
    } catch (_) {
      /* jsdom / unsupported */
    }
    setCurrentTime(seconds);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4 sm:pb-8">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6">
        {/* Header: title on the left, close on the right */}
        <div className="flex items-start justify-between mb-3">
          <div className="pr-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Chapter {chapterOrder}
            </span>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {isOwner && onShare && (
              <button
                onClick={onShare}
                className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                title="Share this chapter"
                aria-label="Share this chapter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary */}
        {summary && (
          <p className={`text-sm text-gray-600 dark:text-gray-400 ${sourceUrl ? 'mb-2' : 'mb-4'}`}>
            {summary}
          </p>
        )}

        {/* Where the text came from, for a chapter built from a link. The
            page is the original article, so it opens in its own tab rather
            than taking the listener away from what is playing. */}
        {sourceUrl && (
          <p className="mb-4">
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={sourceUrl}
              className="link inline-flex items-center gap-1 text-sm"
            >
              Source
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </p>
        )}

        <audio
          ref={audioRef}
          autoPlay={autoPlay}
          className="hidden"
          src={audioUrl}
          onEnded={onEnded}
          onPlay={onPlay}
          onPause={onPause}
          data-testid="chapter-audio"
        >
          Your browser does not support the audio element.
        </audio>

        {/* Seek bar */}
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={1}
          value={Math.min(currentTime, duration || 0)}
          onChange={(e) => seekTo(Number(e.target.value))}
          disabled={!duration}
          aria-label="Seek"
          data-testid="chapter-seek"
          className="w-full h-2 accent-primary cursor-pointer disabled:cursor-not-allowed"
        />
        <div className="mt-1 mb-4 flex justify-between text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          <span data-testid="chapter-current-time">{formatDuration(currentTime)}</span>
          <span>{formatDuration(duration)}</span>
        </div>

        <PlayerControls
          variant="card"
          isPlaying={isPlaying}
          onPlay={play}
          onPause={pause}
          onPrev={onPrev}
          onNext={onNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />

        <div className="mt-4 flex justify-end">
          {isMarkedAsPlayed ? (
            <button
              type="button"
              onClick={onMarkAsUnplayed}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
            >
              Mark as unplayed
            </button>
          ) : (
            <button
              type="button"
              onClick={onMarkAsPlayed}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
            >
              Mark as played
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AudioPlayerOverlay;
